import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { rememberSignedOut } from "@/lib/sso";

import { http } from "../client";
import { keys } from "../keys";
import type { AccountDeleteInput, AccountUpdateInput, LogoutResult, Me, Ok, Session, TermsInput } from "../types";

/** Current session. Also primes the CSRF cookie. Never throws for anonymous users. */
export function useSession() {
  return useQuery({
    queryKey: keys.session,
    queryFn: ({ signal }) => http.get<Session>("/auth/session", signal),
    staleTime: 5 * 60_000,
  });
}

/** Convenience: the signed-in user (undefined while loading, null when anonymous). */
export function useMe(): Me | null | undefined {
  const { data } = useSession();
  return data === undefined ? undefined : data.user;
}

/**
 * On the root site: work in the root site's own courses. Switching to an organisation signs in again through
 * Hume ID instead (`switchOrganisation` in lib/sso).
 * Everything cached belongs to the old one.
 */
export function useSwitchWorkspace() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => http.post<Session>("/auth/workspace", { tenant_id: null }),
    onSuccess: (session) => {
      queryClient.removeQueries({ predicate: (q) => q.queryKey[0] !== keys.session[0] });
      queryClient.setQueryData(keys.session, session);
    },
  });
}

/** Accept the current Terms and Privacy Policy; everything the gate refused is fetched again. */
export function useAcceptTerms() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => http.post<Me>("/account/terms", { accept: true } satisfies TermsInput),
    onSuccess: async (me) => {
      queryClient.setQueryData<Session>(keys.session, (prev) => (prev ? { ...prev, user: me } : prev));
      await queryClient.invalidateQueries({ predicate: (q) => q.queryKey[0] !== keys.session[0] });
    },
  });
}

/** Ends this session; the caller then sends the browser to `redirect` (Hume ID's sign-out, contract §13 row 5). */
export function useLogout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => http.post<LogoutResult>("/auth/logout"),
    onSuccess: () => {
      rememberSignedOut(true);
      queryClient.clear();
    },
  });
}

export function useUpdateAccount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: AccountUpdateInput) => http.patch<Me>("/account", input),
    onSuccess: (me) => {
      queryClient.setQueryData<Session>(keys.session, (prev) => (prev ? { ...prev, user: me } : prev));
    },
  });
}

export function useDeleteAccount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: AccountDeleteInput) => http.post<Ok>("/account/delete", input),
    onSuccess: async () => {
      queryClient.clear();
      await queryClient.invalidateQueries({ queryKey: keys.session });
    },
  });
}

export const ACCOUNT_EXPORT_URL = "/api/account/export";
